import { useEffect, useMemo, useRef, useState, type UIEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Search } from 'lucide-react';
import type { WalletMonitoringItem } from '@shared/api/contracts';
import { Input, Label } from '@shared/ui';
import { cn } from '@shared/lib/utils';
import { formatOperationWalletLabel } from './operation-wallet-select.lib';

interface OperationWalletSelectProps {
  id: string;
  wallets: WalletMonitoringItem[];
  value: string;
  onChange: (walletId: string) => void;
  search: string;
  onSearchChange: (query: string) => void;
  isListLoading?: boolean;
  isLoadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  error?: string;
  /** When false, closes the dropdown and clears the search query. */
  panelOpen?: boolean;
}

export function OperationWalletSelect({
  id,
  wallets,
  value,
  onChange,
  search,
  onSearchChange,
  isListLoading = false,
  isLoadingMore = false,
  hasMore = false,
  onLoadMore,
  error,
  panelOpen = true,
}: OperationWalletSelectProps) {
  const { t } = useTranslation('cabinet');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState<WalletMonitoringItem | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const resolvedSelected = useMemo(() => {
    if (selectedWallet && String(selectedWallet.id) === value) {
      return selectedWallet;
    }
    return wallets.find((wallet) => String(wallet.id) === value) ?? selectedWallet;
  }, [wallets, value, selectedWallet]);

  useEffect(() => {
    if (!panelOpen) {
      onSearchChange('');
      setDropdownOpen(false);
    }
  }, [panelOpen, onSearchChange]);

  useEffect(() => {
    if (!value) {
      setSelectedWallet(null);
    }
  }, [value]);

  useEffect(() => {
    if (!dropdownOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [dropdownOpen]);

  const handleSelect = (wallet: WalletMonitoringItem) => {
    const walletId = String(wallet.id);
    setSelectedWallet(wallet);
    onChange(walletId);
    setDropdownOpen(false);
    onSearchChange('');
  };

  const toggleDropdown = () => {
    setDropdownOpen((prev) => !prev);
  };

  const handleListScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!hasMore || isLoadingMore || !onLoadMore) return;
    const target = event.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 32) {
      onLoadMore();
    }
  };

  return (
    <div className="space-y-1.5 sm:space-y-2">
      <Label htmlFor={id}>{t('serviceAccounting.modal.walletLabel')}</Label>
      <div ref={rootRef} className="relative">
        <button
          id={id}
          type="button"
          aria-expanded={dropdownOpen}
          aria-haspopup="listbox"
          className={cn(
            'flex h-10 w-full items-center justify-between rounded-md border border-input bg-transparent px-3 text-sm shadow-sm',
            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
            !resolvedSelected && 'text-muted-foreground',
            error && 'border-destructive',
          )}
          onClick={toggleDropdown}
        >
          <span className="truncate">
            {resolvedSelected
              ? formatOperationWalletLabel(resolvedSelected)
              : t('serviceAccounting.modal.walletPlaceholder')}
          </span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </button>

        {dropdownOpen && (
          <div
            className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-border bg-popover text-popover-foreground shadow-md"
            role="listbox"
          >
            <div className="border-b border-border p-2">
              <Input
                autoFocus
                icon={<Search className="h-4 w-4" />}
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                placeholder={t('wallets.filters.searchPlaceholder')}
                aria-label={t('wallets.filters.searchPlaceholder')}
                autoComplete="off"
              />
            </div>

            <div
              className="max-h-72 overflow-y-auto overscroll-contain py-1 scroll-smooth"
              onScroll={handleListScroll}
            >
              {isListLoading && (search.trim() || wallets.length === 0) ? (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {t('common.loading', { ns: 'common' })}
                </div>
              ) : wallets.length === 0 ? (
                <div className="px-3 py-6 text-center text-sm text-muted-foreground">
                  {t('serviceAccounting.modal.walletEmpty')}
                </div>
              ) : (
                wallets.map((wallet) => {
                  const walletId = String(wallet.id);
                  const selected = walletId === value;
                  return (
                    <button
                      key={walletId}
                      type="button"
                      role="option"
                      aria-selected={selected}
                      className={cn(
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent',
                        selected && 'bg-accent/60',
                      )}
                      onClick={() => handleSelect(wallet)}
                    >
                      <Check
                        className={cn('h-3.5 w-3.5 shrink-0', selected ? 'opacity-100' : 'opacity-0')}
                      />
                      <span className="truncate">{formatOperationWalletLabel(wallet)}</span>
                    </button>
                  );
                })
              )}
              {isLoadingMore ? (
                <p className="px-3 py-2 text-center text-xs text-muted-foreground">
                  {t('common.loading', { ns: 'common' })}
                </p>
              ) : null}
            </div>
          </div>
        )}
      </div>
      {error && <p className="text-xs text-destructive sm:text-sm">{error}</p>}
    </div>
  );
}
